import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../api';
import TopBar from '../../Tools/Topbar';
import LeftSide from '../../Tools/LeftSidebar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import Pagination from '../../Tools/Pagination';
import { format } from 'date-fns';

const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axiosInstance.get('/ems/get-system-setting', {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('auth_token')}` },
        });
        if (res.data?.status === 'success' && res.data.data.length > 0) {
          const data = res.data.data[0];
          sessionStorage.setItem('date_format', data.date_format || 'DD/MM/YYYY');
          sessionStorage.setItem('time_format', data.time_format || '24');
        }
      } catch (err) {
        sessionStorage.setItem('date_format', 'DD/MM/YYYY');
        sessionStorage.setItem('time_format', '24');
      }
    };
    fetchSettings();
  }, []);
  return null;
};

const Transfercasesforfinalapproval = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [cases, setCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [roleId, setRoleId] = useState(null);
  const [filterName, setFilterName] = useState('');
  const limit = 10;
  const navigate = useNavigate();
  const location = useLocation();
  const { settings, settingsRef, updateSetting, handleColorTheme } = useSettings();

  // Reject Modal State
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Utility function to format date based on settings
      const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      try {
        const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
        const timeFormat = sessionStorage.getItem('time_format') || '24';
    
        // Map WordPress-style formats to date-fns syntax
        const dateFormatMap = {
          'DD/MM/YYYY': 'dd/MM/yyyy',
          'MM/DD/YYYY': 'MM/dd/yyyy',
          'YYYY-MM-DD': 'yyyy-MM-dd',
        };
    
        const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
        const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;
    
        // ✅ Parse both ISO and non-ISO timestamps as *UTC*, not local time
        let utcDate;
        if (dateString.includes('T')) {
          // ISO 8601 with timezone (e.g. 2025-10-27T16:27:19.000000Z)
          utcDate = new Date(dateString);
        } else {
          // MySQL-like (e.g. 2025-10-27 16:35:40)
          utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // force UTC
        }
    
        // Format without applying local timezone
        const utcTimestamp = new Date(
          utcDate.getUTCFullYear(),
          utcDate.getUTCMonth(),
          utcDate.getUTCDate(),
          utcDate.getUTCHours(),
          utcDate.getUTCMinutes(),
          utcDate.getUTCSeconds()
        );
    
        return format(utcTimestamp, formatString);
      } catch (err) {
        console.error('Error formatting date:', err, dateString);
        return 'Invalid Date';
      }
    };

  // Load user info
  useEffect(() => {
    const role = sessionStorage.getItem('Role_id');
    const user = sessionStorage.getItem('user_id');
    if (role && user) {
      setRoleId(role);
      setUserId(user);
    }
  }, [location]);

  // Fetch cases
  const fetchCases = useCallback(async () => {
  if (!roleId || !userId) return;
  setLoading(true);

  try {
    const res = await axiosInstance.get(
      `/ems/records/${roleId}/transfer_cases_final_approval/${userId}`,
      {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
        },
      }
    );

    const rawData = Array.isArray(res.data)
      ? res.data
      : res.data?.data || [];

    // ✅ CLONE + SAFE SORT
    const sortedData = [...rawData].sort((a, b) => {
      const dateA = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA; // DESC
    });

    setCases(sortedData);
    setFilteredCases(sortedData);
  } catch (err) {
    console.error('Fetch cases error:', err);
  } finally {
    setLoading(false);
  }
}, [roleId, userId]);


  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  // Filter by name
  useEffect(() => {
    const filtered = cases.filter(c =>
      c.case_name?.toLowerCase().includes(filterName.toLowerCase())
    );
    setFilteredCases(filtered);
    setPage(1);
  }, [filterName, cases]);

  // Go to details
  const goToDetails = (caseItem) => {
    sessionStorage.setItem('currentCaseId', caseItem.id);
    navigate('/ems/transfer-case-final-approval-details');
  };

  const currentItems = filteredCases.slice((page - 1) * limit, page * limit);

  return (
    <div id="main-wrapper">
      <DateTimeFormatFetcher />
      <LeftSide isSidebarOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <TopBar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings ref={settingsRef} updateSetting={updateSetting} handleColorTheme={handleColorTheme} settings={settings} />
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="card-title fw-semibold">Transfer Cases for Final Approval</h5>
                    <p className="card-subtitle text-muted">Review and approve/reject transfer requests</p>
                  </div>
                </div>

                {/* Filter */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by patient name..."
                      value={filterName}
                      onChange={(e) => setFilterName(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <button className="btn btn-outline-primary" onClick={() => setFilterName('')}>
                      Clear
                    </button>
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive rounded-2 ros-table">
                  <table className="table text-nowrap customize-table mb-0 align-middle">
                    <thead className="text-dark fs-4">
                      <tr>
                        <th><h6 className="fw-semibold mb-0">ID</h6></th>
                        <th><h6 className="fw-semibold mb-0">Patient Name</h6></th>
                        <th><h6 className="fw-semibold mb-0">Transfer From</h6></th>
                        <th><h6 className="fw-semibold mb-0">Transfer To</h6></th>
                        <th><h6 className="fw-semibold mb-0">Created At</h6></th>
                        <th><h6 className="fw-semibold mb-0">Action</h6></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : filteredCases.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-5 text-muted">
                            No cases pending for final approval
                          </td>
                        </tr>
                      ) : (
                        currentItems.map((c) => (
                          <tr key={c.id}>
                            <td>
                              <button
                                className="text-primary border-0 bg-transparent p-0"
                                onClick={() => goToDetails(c)}
                              >
                                {c.id}
                              </button>
                            </td>
                            <td>
                              <button
                                className="text-primary border-0 bg-transparent p-0"
                                onClick={() => goToDetails(c)}
                              >
                                {c.case_name || 'N/A'}
                              </button>
                            </td>
                            <td>{c.from_medical_center?.name || 'N/A'}</td>
                            <td>{c.medical_center?.name || 'N/A'}</td>
                            <td>{formatDate(c.created_at)}</td>
                            <td>
                              <div className="btn-group" role="group">
                                {/* Details */}
                                <button
                                  className="text-success border-0 bg-transparent p-0 me-3"
                                  title="View Details"
                                  onClick={() => goToDetails(c)}
                                >
                                  <i className="ti ti-list-details fs-6"></i>
                                </button>
                                
                                
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {filteredCases.length > limit && (
                  <Pagination
                    total={filteredCases.length}
                    limit={limit}
                    page={page}
                    onPageChange={setPage}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Transfercasesforfinalapproval;